import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useOpenParam } from '../hooks/useOpenParam';
import apiClient from '../api/client';
import { DataGrid, type Column } from '../components/ui/DataGrid';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Label } from '../components/ui/Label';
import { Skeleton } from '../components/ui/Skeleton';
import { formatEUR, formatDate } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';
import { usePermission } from '../auth/usePermission';
import { Permission } from '../auth/permissions';
import { useAuthStore } from '../stores/authStore';
import { Role } from '../auth/roles';
import { Banknote, CalendarClock, Download, Receipt, Wallet, X } from 'lucide-react';

/** WP-7 — the roles allowed to take a counter tender against an open FAC (CTR/POLY/MGR/OWN). */
const REGISTER_ROLES: Role[] = [Role.COUNTER_AGENT, Role.POLYVALENT, Role.MANAGER, Role.OWNER];

interface RegisterReceipt {
  receiptNumber: string;
  method: string;
  totalAmount: number;
  allocations: Array<{ invoiceNumber: string; applied: number }>;
}

interface Recap {
  id: string;
  invoice_number: string;
  recap_reference: string | null;
  customer_id: string | null;
  customer_name: string | null;
  status: string;
  payment_status?: string | null;
  period_start: string | null;
  period_end: string | null;
  issued_at: string;
  due_date: string | null;
  total_ttc: string;
  paid_amount: string | null;
  deductions_ttc: string;
  /** Mid-month counter payments on this recap's own BLs. */
  prepaid_ttc?: string;
  /** Deposits deducted in net and VAT (total_ttc is already net of them). */
  deposit_ttc?: string;
  /** The goods value: total_ttc + deposits. */
  goods_ttc?: number;
  delivery_note_count: string;
  net_to_pay: number;
}

/** Recap invoice (FAC) — the monthly fiscal invoice for DEFERRED (deferred-billing) clients.
 *  Staff can close a period (fan-out over all deferred clients) and record payments. */
export default function RecapInvoices() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  // returnObjects: the month names are one ordered list, not twelve keys to spell out.
  const months = t('recap.months', { returnObjects: true }) as string[];
  const canClose = usePermission(Permission.ACCOUNTING_EXPORT);
  const canViewAll = usePermission(Permission.INVOICE_VIEW_ALL);
  const [rows, setRows] = useState<Recap[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Recap | null>(null);

  const now = new Date();
  // Default to the previous month (the period you'd normally close) — in January, last year's December.
  const [closeYear, setCloseYear] = useState(
    String(now.getUTCMonth() === 0 ? now.getUTCFullYear() - 1 : now.getUTCFullYear())
  );
  const [closeMonth, setCloseMonth] = useState(
    String(now.getUTCMonth() === 0 ? 12 : now.getUTCMonth())
  );
  const [closing, setClosing] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient
      .get<Recap[]>('/recap-invoices')
      .then((r) => setRows(r.data ?? []))
      .catch(showApiError)
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Arriving from the Documents screen with `?open=<id>`: open that recap's panel.
  useOpenParam(rows, loading, setSelected);

  const closePeriod = async () => {
    setClosing(true);
    try {
      const { data } = await apiClient.post<{ closed: number }>(
        '/recap-invoices/close',
        { year: Number(closeYear), month: Number(closeMonth) },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message:
          data.closed > 0 ? t('recap.closed', { count: data.closed }) : t('recap.nothingToBill'),
      });
      load();
    } catch (err) {
      showApiError(err);
    } finally {
      setClosing(false);
    }
  };

  const columns: Column<Recap>[] = [
    {
      key: 'invoice_number',
      header: t('recap.columns.facNo'),
      render: (r) => <span className="font-mono">{r.invoice_number}</span>,
    },
    {
      key: 'recap_reference',
      header: t('recap.columns.reference'),
      render: (r) => r.recap_reference ?? '—',
    },
    { key: 'customer_name', header: t('common.customer'), render: (r) => r.customer_name ?? '—' },
    {
      key: 'period_start',
      header: t('recap.columns.period'),
      render: (r) => `${formatDate(r.period_start)} → ${formatDate(r.period_end)}`,
    },
    {
      key: 'goods_ttc',
      header: t('recap.columns.goodsTtc'),
      render: (r) => formatEUR(r.goods_ttc ?? r.total_ttc),
    },
    {
      key: 'net_to_pay',
      header: t('recap.columns.netDue'),
      render: (r) => formatEUR(r.net_to_pay),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          {enumLabel('invoiceStatus', r.status)}
          {r.payment_status === 'PARTIAL' && (
            <span className="rounded bg-warning/[.13] px-1.5 py-0.5 text-xs font-medium text-warning">
              {t('recap.partial')}
            </span>
          )}
        </span>
      ),
    },
    { key: 'due_date', header: t('recap.columns.due'), render: (r) => formatDate(r.due_date) },
  ];

  return (
    <div>
      <PageHeader
        title={t('recap.title')}
        description={canViewAll ? t('recap.descriptionAll') : t('recap.descriptionOwn')}
      />

      {canClose && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded border bg-card p-4">
          <div className="space-y-1">
            <Label>{t('recap.month')}</Label>
            <Select value={closeMonth} onChange={(e) => setCloseMonth(e.target.value)}>
              {months.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{t('recap.year')}</Label>
            <Input
              value={closeYear}
              onChange={(e) => setCloseYear(e.target.value)}
              inputMode="numeric"
              className="w-28"
            />
          </div>
          <Button variant="secondary" disabled={closing} onClick={closePeriod}>
            <CalendarClock className="h-4 w-4" />{' '}
            {closing ? t('recap.closing') : t('recap.closePeriod')}
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            <Trans i18nKey="recap.closeHint" components={{ 1: <strong /> }} />
          </p>
        </div>
      )}

      <DataGrid
        columns={columns}
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        onRowClick={(r) => setSelected(r)}
      />

      {selected && (
        <RecapDetailPanel
          recap={selected}
          showCustomerLink={canViewAll}
          onClose={() => setSelected(null)}
          onChanged={() => {
            // Reload the list but KEEP the drawer open on the same FAC, so a just-issued
            // NF525 register receipt (and the updated PARTIAL/PAID status) stays on screen.
            apiClient
              .get<Recap[]>('/recap-invoices')
              .then((r) => {
                const next = r.data ?? [];
                setRows(next);
                setSelected((cur) => (cur ? (next.find((x) => x.id === cur.id) ?? cur) : cur));
              })
              .catch(showApiError);
          }}
        />
      )}
    </div>
  );
}

/** Slide-in: recap summary + inline PDF preview + (staff) a "record payment" form that
 *  releases the client's outstanding balance. */
function RecapDetailPanel({
  recap,
  showCustomerLink,
  onClose,
  onChanged,
}: {
  recap: Recap;
  showCustomerLink: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const canPay = usePermission(Permission.PAYMENT_RECONCILE);
  const role = useAuthStore((s) => s.user?.role);
  const canRegister = role != null && REGISTER_ROLES.includes(role);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const remaining = Math.max(0, recap.net_to_pay - Number(recap.paid_amount ?? 0));
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [method, setMethod] = useState('TRANSFER');
  const [paying, setPaying] = useState(false);

  // WP-7 register variant — counter tender against the customer's OPEN FAC(s), oldest-first.
  const [regAmount, setRegAmount] = useState(remaining.toFixed(2));
  const [regMethod, setRegMethod] = useState('CASH');
  const [settling, setSettling] = useState(false);
  const [receipt, setReceipt] = useState<RegisterReceipt | null>(null);

  const settleAtRegister = async () => {
    const amt = Number(regAmount);
    if (!(amt > 0)) {
      showApiError(new Error(t('recap.invalidAmount')));
      return;
    }
    setSettling(true);
    try {
      // Per-customer, oldest-first: settles the customer's oldest open FAC first (which may
      // differ from the one on screen) and issues an NF525 receipt. Creates no sale INV.
      const { data } = await apiClient.post<{ receipt?: RegisterReceipt }>(
        `/recap-invoices/customers/${recap.customer_id}/register-payment`,
        { method: regMethod, amount: amt },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      const r = data?.receipt ?? null;
      setReceipt(r);
      push({
        tone: 'success',
        message: r
          ? t('recap.receiptIssued', {
              number: r.receiptNumber,
              amount: formatEUR(r.totalAmount),
            })
          : t('recap.settledAtRegister', { amount: formatEUR(amt) }),
      });
      onChanged();
    } catch (err) {
      showApiError(err);
    } finally {
      setSettling(false);
    }
  };

  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.get(`/recap-invoices/${recap.id}/pdf`, {
          responseType: 'blob',
        });
        if (!alive) return;
        url = URL.createObjectURL(res.data as Blob);
        setPdfUrl(url);
      } catch (err) {
        showApiError(err);
      }
    })();
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [recap.id, showApiError]);

  const recordPayment = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) {
      showApiError(new Error(t('recap.invalidAmount')));
      return;
    }
    setPaying(true);
    try {
      await apiClient.post(
        `/recap-invoices/${recap.id}/payments`,
        { method, amount: amt },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: t('recap.recorded', { amount: formatEUR(amt) }),
      });
      onChanged();
    } catch (err) {
      showApiError(err);
    } finally {
      setPaying(false);
    }
  };

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex justify-between gap-4 py-0.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <aside className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-background shadow-xl">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="truncate text-lg font-semibold" title={recap.invoice_number}>
            {t('recap.heading', { number: recap.invoice_number })}
          </h2>
          <div className="flex items-center gap-2">
            {pdfUrl && (
              <a
                href={pdfUrl}
                download={`recap-invoice-${recap.invoice_number}.pdf`}
                className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <Download className="h-4 w-4" /> PDF
              </a>
            )}
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground p-1"
              aria-label={t('recap.close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
          <section className="rounded border bg-card p-4">
            <dl className="text-sm">
              <Row label={t('recap.rows.reference')}>{recap.recap_reference ?? '—'}</Row>
              <Row label={t('common.status')}>{enumLabel('invoiceStatus', recap.status)}</Row>
              <Row label={t('common.customer')}>
                {recap.customer_id ? (
                  showCustomerLink ? (
                    <Link
                      to={`/customers/${recap.customer_id}`}
                      className="text-primary hover:underline"
                    >
                      {recap.customer_name ?? recap.customer_id}
                    </Link>
                  ) : (
                    (recap.customer_name ?? recap.customer_id)
                  )
                ) : (
                  '—'
                )}
              </Row>
              <Row label={t('recap.rows.period')}>
                {formatDate(recap.period_start)} → {formatDate(recap.period_end)}
              </Row>
              <Row label={t('recap.rows.deliveryNotes')}>{recap.delivery_note_count}</Row>
              <Row label={t('recap.rows.goodsTtc')}>
                {formatEUR(recap.goods_ttc ?? recap.total_ttc)}
              </Row>
              {Number(recap.deposit_ttc ?? 0) > 0.005 && (
                <Row label={t('recap.rows.deposits')}>− {formatEUR(recap.deposit_ttc)}</Row>
              )}
              {Number(recap.deductions_ttc) > 0.005 && (
                <Row label={t('recap.rows.deductions')}>− {formatEUR(recap.deductions_ttc)}</Row>
              )}
              {Number(recap.prepaid_ttc ?? 0) > 0.005 && (
                <Row label={t('recap.rows.prepaid')}>− {formatEUR(recap.prepaid_ttc)}</Row>
              )}
              <Row label={t('recap.rows.netDue')}>
                <span className="font-semibold">{formatEUR(recap.net_to_pay)}</span>
              </Row>
              <Row label={t('recap.rows.paid')}>{formatEUR(recap.paid_amount ?? 0)}</Row>
              {recap.payment_status && recap.payment_status !== 'PENDING' && (
                <Row label={t('recap.rows.paymentStatus')}>
                  {recap.payment_status === 'PARTIAL' ? (
                    <span className="rounded bg-warning/[.13] px-1.5 py-0.5 text-xs font-medium text-warning">
                      {t('recap.partial')}
                    </span>
                  ) : (
                    enumLabel('paymentStatus', recap.payment_status)
                  )}
                </Row>
              )}
              <Row label={t('recap.rows.dueDate')}>{formatDate(recap.due_date)}</Row>
            </dl>

            {canPay && recap.status !== 'PAID' && remaining > 0 && (
              <div className="mt-4 flex flex-wrap items-end gap-3 border-t pt-4">
                <div className="space-y-1">
                  <Label>{t('recap.amount')}</Label>
                  <Input
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputMode="decimal"
                    className="w-32"
                  />
                </div>
                <div className="space-y-1">
                  <Label>{t('recap.method')}</Label>
                  <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                    {['TRANSFER', 'CARD', 'CHECK', 'CASH'].map((m) => (
                      <option key={m} value={m}>
                        {enumLabel('paymentMethod', m)}
                      </option>
                    ))}
                  </Select>
                </div>
                <Button variant="success" disabled={paying} onClick={recordPayment}>
                  <Banknote className="h-4 w-4" />
                  {paying ? t('recap.recording') : t('recap.recordPayment')}
                </Button>
              </div>
            )}

            {/* WP-7 — counter settlement of the customer's OPEN FAC(s) with a counter tender.
                Oldest-first across all their open FACs; issues an NF525 receipt; creates no INV. */}
            {canRegister && recap.customer_id && (
              <div className="mt-4 border-t pt-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <Receipt className="h-4 w-4" /> {t('recap.register')}
                </div>
                <p className="mb-3 text-xs text-muted-foreground">{t('recap.registerHint')}</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label>{t('recap.amount')}</Label>
                    <Input
                      value={regAmount}
                      onChange={(e) => setRegAmount(e.target.value)}
                      inputMode="decimal"
                      className="w-32"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>{t('recap.tender')}</Label>
                    <Select value={regMethod} onChange={(e) => setRegMethod(e.target.value)}>
                      {['CASH', 'CARD', 'CHECK'].map((m) => (
                        <option key={m} value={m}>
                          {enumLabel('paymentMethod', m)}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <Button disabled={settling} onClick={settleAtRegister}>
                    <Wallet className="h-4 w-4" />
                    {settling ? t('recap.settling') : t('recap.settle')}
                  </Button>
                </div>

                {receipt && (
                  <div className="mt-3 rounded border border-success/40 bg-success/5 p-3 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-success">
                      <Receipt className="h-4 w-4" />{' '}
                      {t('recap.receiptHeading', { number: receipt.receiptNumber })}
                    </div>
                    <dl className="mt-2 space-y-0.5">
                      <Row label={t('recap.tender')}>
                        {enumLabel('paymentMethod', receipt.method)}
                      </Row>
                      <Row label={t('recap.totalSettled')}>{formatEUR(receipt.totalAmount)}</Row>
                      {receipt.allocations?.map((a) => (
                        <Row
                          key={a.invoiceNumber}
                          label={t('recap.imputedTo', { invoice: a.invoiceNumber })}
                        >
                          {formatEUR(a.applied)}
                        </Row>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="flex min-h-[24rem] flex-1 flex-col rounded border bg-card">
            <h3 className="border-b px-3 py-2 text-sm font-semibold">{t('recap.document')}</h3>
            {pdfUrl ? (
              <iframe
                title={t('recap.pdfFrameTitle', { number: recap.invoice_number })}
                src={pdfUrl}
                className="h-full w-full flex-1 rounded-b"
              />
            ) : (
              <div className="p-4">
                <Skeleton className="h-80 w-full" />
              </div>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
